import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationVariant = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  variant: NotificationVariant;
  title?: string;
  duration?: number;
  persistent?: boolean;
  createdAt: number;
}

export interface TablePreferences {
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  columnSizing?: Record<string, number>;
  pageSize?: number;
  sorting?: { id: string; desc: boolean }[];
}

// ─── State ────────────────────────────────────────────────────────────────────

interface UIState {
  sidebarCollapsed: boolean;
  utilityPanelOpen: boolean;
  utilityPanelTab: 'notifications' | 'shortcuts' | 'recent';
  mobileMenuOpen: boolean;
  globalSearchOpen: boolean;
  shortcutHelpOpen: boolean;
  notifications: Notification[];
  unreadNotificationsCount: number;
  tablePreferences: Record<string, TablePreferences>;
  /** When true, a full-screen blocking overlay is shown. */
  globalLoading: boolean;
  /** Optional message shown inside the loading overlay. */
  globalLoadingMessage: string | null;
}

interface UIActions {
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  openUtilityPanel: (tab?: UIState['utilityPanelTab']) => void;
  closeUtilityPanel: () => void;
  toggleUtilityPanel: (tab?: UIState['utilityPanelTab']) => void;
  openMobileMenu: () => void;
  closeMobileMenu: () => void;
  openGlobalSearch: () => void;
  closeGlobalSearch: () => void;
  toggleGlobalSearch: () => void;
  openShortcutHelp: () => void;
  closeShortcutHelp: () => void;
  addNotification: (n: Omit<Notification, 'id' | 'createdAt'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setUnreadNotificationsCount: (count: number) => void;
  setTablePreferences: (tableId: string, prefs: Partial<TablePreferences>) => void;
  getTablePreferences: (tableId: string) => TablePreferences;
  setGlobalLoading: (loading: boolean, message?: string) => void;
}

type UIStore = UIState & UIActions;

let _notifIdSeq = 0;

// ─── Store ────────────────────────────────────────────────────────────────────

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        sidebarCollapsed: false,
        utilityPanelOpen: false,
        utilityPanelTab: 'notifications' as const,
        mobileMenuOpen: false,
        globalSearchOpen: false,
        shortcutHelpOpen: false,
        notifications: [],
        unreadNotificationsCount: 0,
        tablePreferences: {},
        globalLoading: false,
        globalLoadingMessage: null,

        toggleSidebar: () =>
          set((state) => {
            state.sidebarCollapsed = !state.sidebarCollapsed;
          }),

        setSidebarCollapsed: (collapsed) =>
          set((state) => {
            state.sidebarCollapsed = collapsed;
          }),

        openUtilityPanel: (tab) =>
          set((state) => {
            state.utilityPanelOpen = true;
            if (tab) state.utilityPanelTab = tab;
          }),

        closeUtilityPanel: () =>
          set((state) => {
            state.utilityPanelOpen = false;
          }),

        toggleUtilityPanel: (tab) =>
          set((state) => {
            if (state.utilityPanelOpen && (!tab || tab === state.utilityPanelTab)) {
              state.utilityPanelOpen = false;
            } else {
              state.utilityPanelOpen = true;
              if (tab) state.utilityPanelTab = tab;
            }
          }),

        openMobileMenu: () =>
          set((state) => {
            state.mobileMenuOpen = true;
          }),

        closeMobileMenu: () =>
          set((state) => {
            state.mobileMenuOpen = false;
          }),

        openGlobalSearch: () =>
          set((state) => {
            state.globalSearchOpen = true;
          }),

        closeGlobalSearch: () =>
          set((state) => {
            state.globalSearchOpen = false;
          }),

        toggleGlobalSearch: () =>
          set((state) => {
            state.globalSearchOpen = !state.globalSearchOpen;
          }),

        openShortcutHelp: () =>
          set((state) => {
            state.shortcutHelpOpen = true;
          }),

        closeShortcutHelp: () =>
          set((state) => {
            state.shortcutHelpOpen = false;
          }),

        addNotification: (notification) => {
          const id = String(++_notifIdSeq);
          set((state) => {
            state.notifications.push({ ...notification, id, createdAt: Date.now() });
          });
          return id;
        },

        removeNotification: (id) =>
          set((state) => {
            state.notifications = state.notifications.filter((n) => n.id !== id);
          }),

        clearNotifications: () =>
          set((state) => {
            state.notifications = [];
          }),

        setUnreadNotificationsCount: (count) =>
          set((state) => {
            state.unreadNotificationsCount = count;
          }),

        setTablePreferences: (tableId, prefs) =>
          set((state) => {
            state.tablePreferences[tableId] = {
              ...state.tablePreferences[tableId],
              ...prefs,
            };
          }),

        getTablePreferences: (tableId) => get().tablePreferences[tableId] ?? {},

        setGlobalLoading: (loading, message) =>
          set((state) => {
            state.globalLoading = loading;
            state.globalLoadingMessage = loading ? (message ?? null) : null;
          }),
      })),
      {
        name: 'garagewale-ui',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          utilityPanelTab: state.utilityPanelTab,
          tablePreferences: state.tablePreferences,
          unreadNotificationsCount: state.unreadNotificationsCount,
        }),
      },
    ),
    { name: 'UIStore' },
  ),
);
